import { Card, CardBody, Col, Row } from "reactstrap";
import { H3, LI, UL } from "../../AbstractElements";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { MoreVertical } from "react-feather";
import { showWarningAlert } from "../../utils/showWarningAlert";
 import { useState, useMemo } from "react";
import SearchBar from "../../common-elements/search-bar/SearchBar";

// Interfaces
interface Benefit {
  id: number | string;
  description: string;
}

interface MemberPlan {
  id: number;
  type: string;
  subscriptionType: string;
  activationPrice: number;
  monthlyPrice: number;
  yearlyPrice: number;
  benefit: Benefit[];
  isActive: boolean;
}

// Dummy Data
const becomeMemberData: MemberPlan[] = [
  {
    id: 1,
    type: "Linnk Hub",
    subscriptionType: "periodically",
    activationPrice: 150,
    monthlyPrice: 50,
    yearlyPrice: 500,
    benefit: [
      { id: 101, description: "Access to basic features" },
      { id: 102, description: "5 projects limit" },
      { id: 103, description: "Email support" },
    ],
    isActive: true,
  },
  {
    id: 2,
    type: "Linnk Me",
    subscriptionType: "periodically",
    activationPrice: 150,
    monthlyPrice: 50,
    yearlyPrice: 500,
    benefit: [
      { id: 201, description: "Access to all features" },
      { id: 202, description: "Unlimited projects" },
      { id: 203, description: "Priority email support" },
      { id: 204, description: "Monthly reports" },
    ],
    isActive: false,
  },
  {
    id: 3,
    type: "Linnk Up",
    subscriptionType: "periodically",
    activationPrice: 150,
    monthlyPrice: 50,
    yearlyPrice: 500,
    benefit: [
      { id: 301, description: "All Pro features" },
      { id: 302, description: "Dedicated account manager" },
      { id: 303, description: "Custom integrations" },
      { id: 304, description: "Weekly reports" },
      { id: 305, description: "API access" },
    ],
    isActive: true,
  },
  {
    id: 4,
    type: "HRMS",
    subscriptionType: "onetime",
    activationPrice: 300,
    monthlyPrice: 0,
    yearlyPrice: 0,
    benefit: [
      { id: 401, description: "All Pro features" },
      { id: 402, description: "Dedicated account manager" },
      { id: 403, description: "Custom integrations" },
      { id: 404, description: "Weekly reports" },
      { id: 405, description: "API access" },
    ],
    isActive: true,
  },
  {
    id: 5,
    type: "Finance/Accounts",
    subscriptionType: "periodically",
    activationPrice: 150,
    monthlyPrice: 50,
    yearlyPrice: 500,
    benefit: [
      { id: 501, description: "All Pro features" },
      { id: 502, description: "Dedicated account manager" },
      { id: 503, description: "Custom integrations" },
      { id: 504, description: "Weekly reports" },
      { id: 505, description: "API access" },
    ],
    isActive: false,
  },
  {
    id: 6,
    type: "White Labeling",
    subscriptionType: "onetime",
    activationPrice: 300,
    monthlyPrice: 0,
    yearlyPrice: 0,
    benefit: [
      { id: 601, description: "All Pro features" },
      { id: 602, description: "Dedicated account manager" },
      { id: 603, description: "Custom integrations" },
      { id: 604, description: "Weekly reports" },
      { id: 605, description: "API access" },
    ],
    isActive: true,
  },
  {
    id: 7,
    type: "App White Labeling",
    subscriptionType: "periodically",
    activationPrice: 150,
    monthlyPrice: 50,
    yearlyPrice: 500,
    benefit: [
      { id: 701, description: "All Pro features" },
      { id: 702, description: "Dedicated account manager" },
      { id: 703, description: "Custom integrations" },
      { id: 704, description: "Weekly reports" },
      { id: 705, description: "API access" },
    ],
    isActive: false,
  },
];

const AddonCard = () => {
  const [searchText, setSearchText] = useState("");
  const { t } = useTranslation();

  const filteredPlans = useMemo(() => {
    const searchLower = searchText.trim().toLowerCase();

    if (!searchLower) return becomeMemberData;

    return becomeMemberData.filter((plan) => {
      const matchesPlan =
        plan.type.toLowerCase().includes(searchLower) ||
        plan.subscriptionType.toLowerCase().includes(searchLower);

      const matchesBenefit = plan.benefit.some((benefit) =>
        benefit.description.toLowerCase().includes(searchLower)
      );

      return matchesPlan || matchesBenefit;
    });
  }, [searchText]);

  // Action menu component for each plan
  const PlanActions: React.FC<{ plan: MemberPlan }> = ({ plan }) => {
    const handleDelete = () => {
      showWarningAlert({
        title: t("Are you sure?"),
        text: `Do you want to delete ${plan.type}?`,
        confirmButtonText: t("Delete"),
        onConfirm: () => {
          alert(t("Delete functionality is disabled as data is static."));
        },
      });
    };

    const handleToggleStatus = () => {
      showWarningAlert({
        title: t("Are you sure?"),
        text: `Do you want to ${plan.isActive ? "deactivate" : "activate"} ${
          plan.type
        }?`,
        confirmButtonText: plan.isActive ? t("Deactivate") : t("Activate"),
        onConfirm: () => {
          alert(
            t("Toggle status functionality is disabled as data is static.")
          );
        },
      });
    };

    return (
      <UncontrolledDropdown>
        <DropdownToggle tag="span" className="cursor-pointer">
          <MoreVertical size={18} />
        </DropdownToggle>
        <DropdownMenu>
          <Link to={`${process.env.PUBLIC_URL}/addons/edit/${plan.id}`}>
            <DropdownItem>{t("Edit")}</DropdownItem>
          </Link>
          <DropdownItem onClick={handleDelete}>{t("Delete")}</DropdownItem>
          <DropdownItem onClick={handleToggleStatus}>
            {plan.isActive ? t("Deactivate") : t("Activate")}
          </DropdownItem>
        </DropdownMenu>
      </UncontrolledDropdown>
    );
  };

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-4">
          <SearchBar
            label={t("Search")}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t("Search by Plan Name")}
          />
          <Link
            className="btn btn-primary d-flex align-items-center"
            to={`${process.env.PUBLIC_URL}/addons/add`}
          >
            <i className="fa fa-plus me-2" />
            {t("Add")}
          </Link>
        </div>

        {filteredPlans.length === 0 ? (
          <div className="text-center py-4">
            <p>{t("There are no records to display")}</p>
          </div>
        ) : (
          <Row className="g-4">
            {filteredPlans.map((item: MemberPlan) => (
              <Col lg="3" md="6" sm="12" key={item.id} className="mb-4 d-flex">
                <div className="pricingtable pricing-block w-100 border border-1 rounded-2 position-relative shadow-sm">
                  <div className="pricingtable-header p-3">
                    <H3 className="title text-center">{item.type}</H3>
                    <div style={{ position: "absolute", top: 10, right: 10 }}>
                      <PlanActions plan={item} />
                    </div>
                  </div>

                  <div className="p-3">
                    {item.subscriptionType === "onetime" ? (
                      <div className="d-flex justify-content-center">
                        <div
                          className="rounded-circle bg-primary text-white d-flex flex-column align-items-center justify-content-center border border-1"
                          style={{ width: "120px", height: "120px" }}
                        >
                          <div className="fw-bold fs-4">
                            ${item.activationPrice}
                          </div>
                          <small className="text-white-50">Activation</small>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div className="d-flex justify-content-center mb-3">
                          <div
                            className="rounded-circle bg-primary text-white d-flex flex-column align-items-center justify-content-center border border-1"
                            style={{ width: "120px", height: "120px" }}
                          >
                            <div className="fw-bold fs-4">
                              ${item.activationPrice}
                            </div>
                            <small className="text-white-50">
                              {t("Activation")}
                            </small>{" "}
                          </div>
                        </div>
                        <div className="d-flex justify-content-between ">
                          <div className="position-relative p-3 rounded-2 text-center w-50 me-2 overflow-hidden">
                            <div
                              className="position-absolute top-0 start-0 w-100 h-100 bg-primary"
                              style={{ opacity: 0.3, zIndex: 0 }}
                            ></div>
                            <div className="position-relative z-1">
                              <div className="fw-bold fs-8">
                                ${item.yearlyPrice}
                                <small className="text-muted">
                                  {t("/yr")}
                                </small>{" "}
                              </div>
                            </div>
                          </div>
                          <div className="position-relative p-3 rounded-2 text-center w-50 me-2 overflow-hidden">
                            <div
                              className="position-absolute top-0 start-0 w-100 h-100 bg-primary"
                              style={{ opacity: 0.3, zIndex: 0 }}
                            ></div>
                            <div className="position-relative z-1">
                              <div className="fw-bold fs-8">
                                ${item.monthlyPrice}
                                <small className="text-muted">
                                  {t("/mo")}
                                </small>{" "}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <UL className="pricing-content simple-list px-3">
                    {item.benefit.map((benefit) => (
                      <LI key={`${item.id}-${benefit.id}`}>
                        {benefit.description}
                      </LI>
                    ))}
                  </UL>

                  <div
                    className={`position-absolute top-0 start-0 px-3 py-1 text-white fw-semibold rounded-bottom-end ${
                      item.isActive ? "bg-success" : "bg-danger"
                    }`}
                    style={{ fontSize: "0.75rem", zIndex: 10 }}
                  >
                    {item.isActive ? t("Active") : t("Inactive")}
                  </div>
                </div>
              </Col>
            ))}
          </Row>
        )}
      </CardBody>
    </Card>
  );
};

export default AddonCard;
